import { FC, useMemo } from 'react';
import { ProjectCardDraftLayout, ProjectCardDraftLayoutNew } from './ProjectCardDraftLayout';
import { ProjectEvents } from '../../helpers/projectEvents';
import {ProjectCardDraftTitle, ProjectCardTitle} from './styles';
import { useTranslation } from 'react-i18next';
import { Colors, CSpan, FlexBox, useBreakpoint, useIsMobile } from '@brg/ui';
import { ProjectCardTools } from './ProjectCardTools';
import { useHistory } from 'react-router-dom';
import { ProjectTooltipProps } from '../tooltips/ProjectTooltip';
import { IDraft } from '../../common';
import { CreateLinkTo } from '../../constants/linkTo';
import { DeleteButton } from '../DeleteButton/DeleteButton';
import { useTheme } from 'styled-components';
import { LayoutConst } from 'core';
import {AppTester} from "../../../supporting";

export interface ProjectCardDraftProps extends IDraft, ProjectEvents {
    isLoading?: boolean;
    tooltip?: ProjectTooltipProps;
    projectType?: string;
    projectContexts?: string[];
}

export const ProjectCardDraft: FC<ProjectCardDraftProps> = (props) => {
    const { isLoading, identifier, title, image, tooltip, projectType, ...handlers } = props;
    const { t } = useTranslation();
    const history = useHistory();

    const onProjectClick = (projectId: string): void => history.push(CreateLinkTo.projectEdit(projectId));
    return (
        <ProjectCardDraftLayout
            identifier={identifier}
            onClick={handlers.onView ?? onProjectClick}
            img={image}
            isLoading={isLoading}
            title={
                <ProjectCardTitle>
                    {title}&nbsp;<CSpan color={Colors.geoWhite47}>{t('draft')}</CSpan>
                </ProjectCardTitle>
            }
            more={
                <ProjectCardTools
                    {...handlers}
                    isHiddenCancel={true}
                    identifier={identifier}
                    isEditable={true}
                    isDeletable={true}
                    projectType={projectType}
                    {...(tooltip ?? {})}
                />
            }
        />
    );
};

export const ProjectCardDraftNew: FC<ProjectCardDraftProps> = (props) => {
    const { isLoading, identifier, title, image, tooltip, projectType, projectContexts, ...handlers } = props;
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const history = useHistory();
    const theme = useTheme();
    const isAgeyTomesh = AppTester.isAgeytomesh();
    const isHSEDesign = AppTester.isHSEDesign();


    const onProjectClick = (projectId: string) =>  {
        if (projectContexts?.includes('medialecture')) {
            window.open('https://education.mediiia.com' + '/edit/' + identifier)
        }
        if (isAgeyTomesh) {
            if (projectType && ['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(projectType)) {
                window.open(process.env.REACT_APP_URL_DEZIIIGN_PRO + '/edit/' + identifier)
            } else {
                window.open((projectContexts?.includes('hsedesign') ? process.env.REACT_APP_URL_HSEDESIGN_RU : process.env.REACT_APP_URL_DEZIIIGN) + '/edit/' + identifier)
            }
            return;
        }
        if (projectContexts?.includes('lmsproject')) {
            const currentOrigin = window.location.origin;
            window.open(currentOrigin + '/edit/' + identifier)
        }
        if (projectContexts?.includes('lmshseproject')) {
            const currentOrigin = window.location.origin;
            window.open(currentOrigin + '/edit/' + identifier)
        }

        else if (projectType && ['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(projectType)) {
            if (projectContexts?.includes('hsedesign')) {
                const hseProUrl = window.location.origin.includes("com") 
                    ? process.env.REACT_APP_URL_HSEDESIGN_PRO_COM 
                    : process.env.REACT_APP_URL_HSEDESIGN_PRO_RU;
                const baseUrl = hseProUrl?.endsWith('/') ? hseProUrl.slice(0, -1) : hseProUrl;
                window.open(`${baseUrl}/edit/${identifier}`)
            } 
            else {
                const hseUrl = process.env.REACT_APP_URL_HSEDESIGN_PRO_COM?.endsWith('/') ? 
                    process.env.REACT_APP_URL_HSEDESIGN_PRO_COM.slice(0, -1) :  
                    process.env.REACT_APP_URL_HSEDESIGN_PRO_COM;

                const proUrl = process.env.REACT_APP_URL_DEZIIIGN_PRO_COM?.endsWith('/') 
                    ? process.env.REACT_APP_URL_DEZIIIGN_PRO_COM.slice(0, -1) 
                    : process.env.REACT_APP_URL_DEZIIIGN_PRO_COM;

                const url = isHSEDesign ? hseUrl : proUrl;
                window.open(`${url}/edit/${identifier}`)
            }
        } 
        else {
            history.push(CreateLinkTo.projectEdit(projectId))
        }
    }

    const isPsn = useMemo(() => (projectType && ['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(projectType)), [projectType])

    const wideTypes = useMemo(() => (projectType && ['t-shape', 'fixed-ratio-large'].includes(projectType)), [projectType])
   
    const moreTools = useMemo(() => ({
        delete: isMobile && <DeleteButton onClick={() => handlers?.onDelete && handlers?.onDelete(identifier)}/>,
        more: !isMobile && <ProjectCardTools
            {...handlers}
            isHiddenCancel={true}
            identifier={identifier}
            isPresentation={Boolean(isPsn)}
            isEditable={!isAgeyTomesh}
            isDeletable={true}
            projectType={projectType}
            {...(tooltip ?? {})}
        />
    }),[isMobile, handlers, tooltip, projectType, identifier])

    return (
        <ProjectCardDraftLayoutNew
            identifier={identifier}
            onClick={handlers.onView ?? onProjectClick}
            img={image}
            hasTitle={title?.length > 0}
            isLoading={isLoading}
            projectType={projectType}
            title={
                <>
                    {isPsn ? (
                        <FlexBox gy={8} direction='column' directionMobile='column' className={'psn-draft-title'}>
                            {title?.length > 0 && <ProjectCardTitle shouldUseTheme={!image}>
                                <CSpan color={!!wideTypes ? (is992 ? Colors.geoWhite100 : theme.draftCreate.draftCards.psn_card.color) : Colors.geoWhite100}>{title}</CSpan>
                            </ProjectCardTitle>}
                            <ProjectCardTitle>
                                <ProjectCardDraftTitle color={(image && !wideTypes) ? Colors.galBlack31 : Colors.galBlack47}>{t('draft')}</ProjectCardDraftTitle>
                            </ProjectCardTitle>
                        </FlexBox>
                    ) : (
                        <ProjectCardTitle shouldUseTheme={!image}>
                            <span>{title}</span>
                            <ProjectCardDraftTitle color={image ? Colors.galBlack31 : Colors.galBlack47}>{t('draft')}</ProjectCardDraftTitle>
                        </ProjectCardTitle>
                    )}
                </>
            }
            {...moreTools}
        />
    );
};
import { FC, useMemo } from 'react';
import { ProjectCardDraftLayout, ProjectCardDraftLayoutNew } from './ProjectCardDraftLayout';
import { ProjectEvents } from '../../helpers/projectEvents';
import {ProjectCardDraftTitle, ProjectCardTitle} from './styles';
import { useTranslation } from 'react-i18next';
import { Colors, CSpan, FlexBox, useBreakpoint, useIsMobile } from '@brg/ui';
import { ProjectCardTools } from './ProjectCardTools';
import { useHistory } from 'react-router-dom';
import { ProjectTooltipProps } from '../tooltips/ProjectTooltip';
import { IDraft } from '../../common';
import { CreateLinkTo } from '../../constants/linkTo';
import { DeleteButton } from '../DeleteButton/DeleteButton';
import { useTheme } from 'styled-components';
import { LayoutConst } from 'core';
import {AppTester} from "../../../supporting";

export interface ProjectCardDraftProps extends IDraft, ProjectEvents {
    isLoading?: boolean;
    tooltip?: ProjectTooltipProps;
    projectType?: string;
    projectContexts?: string[];
}

export const ProjectCardDraft: FC<ProjectCardDraftProps> = (props) => {
    const { isLoading, identifier, title, image, tooltip, projectType, ...handlers } = props;
    const { t } = useTranslation();
    const history = useHistory();

    const onProjectClick = (projectId: string): void => history.push(CreateLinkTo.projectEdit(projectId));
    return (
        <ProjectCardDraftLayout
            identifier={identifier}
            onClick={handlers.onView ?? onProjectClick}
            img={image}
            isLoading={isLoading}
            title={
                <ProjectCardTitle>
                    {title}&nbsp;<CSpan color={Colors.geoWhite47}>{t('draft')}</CSpan>
                </ProjectCardTitle>
            }
            more={
                <ProjectCardTools
                    {...handlers}
                    isHiddenCancel={true}
                    identifier={identifier}
                    isEditable={true}
                    isDeletable={true}
                    projectType={projectType}
                    {...(tooltip ?? {})}
                />
            }
        />
    );
};

export const ProjectCardDraftNew: FC<ProjectCardDraftProps> = (props) => {
    const { isLoading, identifier, title, image, tooltip, projectType, projectContexts, ...handlers } = props;
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const history = useHistory();
    const theme = useTheme();
    const isAgeyTomesh = AppTester.isAgeytomesh();
    const isHSEDesign = AppTester.isHSEDesign();


    const onProjectClick = (projectId: string) =>  {
        if (projectContexts?.includes('medialecture')) {
            window.open('https://education.mediiia.com' + '/edit/' + identifier)
        }
        if (isAgeyTomesh) {
            if (projectType && ['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(projectType)) {
                window.open(process.env.REACT_APP_URL_DEZIIIGN_PRO + '/edit/' + identifier)
            } else {
                window.open((projectContexts?.includes('hsedesign') ? process.env.REACT_APP_URL_HSEDESIGN_RU : process.env.REACT_APP_URL_DEZIIIGN) + '/edit/' + identifier)
            }
            return;
        }
        if (projectContexts?.includes('lmsproject')) {
            const currentOrigin = window.location.origin;
            window.open(currentOrigin + '/edit/' + identifier)
        }
        if (projectContexts?.includes('lmshseproject')) {
            const currentOrigin = window.location.origin;
            window.open(currentOrigin + '/edit/' + identifier)
        }

        else if (projectType && ['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(projectType)) {
            if (projectContexts?.includes('hsedesign')) {
                const hseProUrl = window.location.origin.includes("com") 
                    ? process.env.REACT_APP_URL_HSEDESIGN_PRO_COM 
                    : process.env.REACT_APP_URL_HSEDESIGN_PRO_RU;
                const baseUrl = hseProUrl?.endsWith('/') ? hseProUrl.slice(0, -1) : hseProUrl;
                window.open(`${baseUrl}/edit/${identifier}`)
            } 
            else {
                const hseUrl = process.env.REACT_APP_URL_HSEDESIGN_PRO_COM?.endsWith('/') ? 
                    process.env.REACT_APP_URL_HSEDESIGN_PRO_COM.slice(0, -1) :  
                    process.env.REACT_APP_URL_HSEDESIGN_PRO_COM;

                const proUrl = process.env.REACT_APP_URL_DEZIIIGN_PRO_COM?.endsWith('/') 
                    ? process.env.REACT_APP_URL_DEZIIIGN_PRO_COM.slice(0, -1) 
                    : process.env.REACT_APP_URL_DEZIIIGN_PRO_COM;

                const url = isHSEDesign ? hseUrl : proUrl;
                window.open(`${url}/edit/${identifier}`)
            }
        } 
        else {
            history.push(CreateLinkTo.projectEdit(projectId))
        }
    }

    const isPsn = useMemo(() => (projectType && ['fixed-ratio-large', 'fixed-ratio-small', 't-shape']?.includes(projectType)), [projectType])

    const wideTypes = useMemo(() => (projectType && ['t-shape', 'fixed-ratio-large'].includes(projectType)), [projectType])
   
    const moreTools = useMemo(() => ({
        delete: isMobile && <DeleteButton onClick={() => handlers?.onDelete && handlers?.onDelete(identifier)}/>,
        more: !isMobile && <ProjectCardTools
            {...handlers}
            isHiddenCancel={true}
            identifier={identifier}
            isPresentation={Boolean(isPsn)}
            isEditable={!isAgeyTomesh}
            isDeletable={true}
            projectType={projectType}
            {...(tooltip ?? {})}
        />
    }),[isMobile, handlers, tooltip, projectType, identifier])

    return (
        <ProjectCardDraftLayoutNew
            identifier={identifier}
            onClick={handlers.onView ?? onProjectClick}
            img={image}
            hasTitle={title?.length > 0}
            isLoading={isLoading}
            projectType={projectType}
            title={
                <>
                    {isPsn ? (
                        <FlexBox gy={8} direction='column' directionMobile='column' className={'psn-draft-title'}>
                            {title?.length > 0 && <ProjectCardTitle shouldUseTheme={!image}>
                                <CSpan color={!!wideTypes ? (is992 ? Colors.geoWhite100 : theme.draftCreate.draftCards.psn_card.color) : Colors.geoWhite100}>{title}</CSpan>
                            </ProjectCardTitle>}
                            <ProjectCardTitle>
                                <ProjectCardDraftTitle color={(image && !wideTypes) ? Colors.galBlack31 : Colors.galBlack47}>{t('draft')}</ProjectCardDraftTitle>
                            </ProjectCardTitle>
                        </FlexBox>
                    ) : (
                        <ProjectCardTitle shouldUseTheme={!image}>
                            <span>{title}</span>
                            <ProjectCardDraftTitle color={image ? Colors.galBlack31 : Colors.galBlack47}>{t('draft')}</ProjectCardDraftTitle>
                        </ProjectCardTitle>
                    )}
                </>
            }
            {...moreTools}
        />
    );
};
